import { yupResolver } from "@hookform/resolvers/yup";
import { XIcon } from "lucide-react";
import { type FC, useCallback, useEffect } from "react";
import {
  FormProvider,
  type SubmitHandler,
  useForm,
  useWatch,
} from "react-hook-form";
import * as yup from "yup";
import { ButtonUI } from "../../../../components/Button";
import { FormGroupUI } from "../../../../components/FormGroup";
import { FormGroupLabelUI } from "../../../../components/FormGroupLabel";
import { ReactSelectUI } from "../../../../components/rhfs/ReactSelect";
import { DEFAULT_SEARCH_FILTER } from "../../../../constants/search-fiilter";
import {
  type EmailSearchInput,
  RecruitmentType,
} from "../../../../hooks/useEmailsRestApi";
import { HRTypeFormUI } from "./HRTypeForm/HRTypeForm";
import { MatterTypeFormUI } from "./MatterTypeForm/MatterTypeForm";

const recruitmentTypeOptions = [
  { label: "案件", value: RecruitmentType.Matter },
  { label: "人材", value: RecruitmentType.HumanResource },
];

export type SearchEmailsFormUIProps = {
  isLoading: boolean;
  currentSearchInput: EmailSearchInput;
  isSearchFilterOpen: boolean;
  onSearch: SubmitHandler<EmailSearchInput>;
  onClearFilter: () => void;
  onClose: () => void;
};

export const SearchEmailsFormUI: FC<SearchEmailsFormUIProps> = ({
  isLoading,
  currentSearchInput,
  onSearch,
  onClearFilter,
  onClose,
}) => {
  const schema = yup
    .object()
    .shape({
      recruitmentType: yup.string().required(),
      hrFilter: yup.object().shape({
        genderType: yup.string().nullable(),
        ageRange: yup.object().shape({
          from: yup.number(),
          to: yup.number(),
        }),
        rewardRange: yup.object().shape({
          from: yup.number(),
          to: yup.number(),
        }),
        hrTypes: yup.object().shape({
          freerance: yup.boolean(),
          contractEmployee: yup.boolean(),
          fullTimeEmployee: yup.boolean(),
        }),
        immediately: yup.boolean(),
        needRemote: yup.boolean(),
        onlyJapanese: yup.boolean(),
      }),
      matterFilter: yup.object().shape({
        rewardRange: yup.object().shape({
          from: yup.number(),
          to: yup.number(),
        }),
        distanceFromEnd: yup.number(),
        oneOpportunity: yup.boolean(),
        otherCountriesEnabled: yup.boolean(),
        onlyEnableFreelance: yup.boolean(),
        enableRemote: yup.boolean(),
      }),
      limitedByDay: yup.number(),
      orderType: yup.string(),
      feewords: yup.array().of(yup.string()),
      skills: yup.array().of(yup.string()),
      favoritesOnly: yup.boolean(),
    })
    .required();

  const methods = useForm<EmailSearchInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...DEFAULT_SEARCH_FILTER,
      ...currentSearchInput,
      recruitmentType:
        currentSearchInput.recruitmentType ?? RecruitmentType.Matter,
    },
  });

  const recruitmentType = useWatch({
    control: methods.control,
    name: "recruitmentType",
    defaultValue: currentSearchInput.recruitmentType ?? RecruitmentType.Matter,
  });

  const isMatter = recruitmentType === RecruitmentType.Matter;

  useEffect(() => {
    methods.reset({
      ...DEFAULT_SEARCH_FILTER,
      ...currentSearchInput,
      recruitmentType:
        currentSearchInput.recruitmentType ?? RecruitmentType.Matter,
    });
  }, [currentSearchInput, methods]);

  // 検索対象（案件/人材）切り替え時に、フィルターをクリアする
  const handleRecruitmentTypeChange = useCallback(
    (value: string) => {
      methods.reset({
        ...DEFAULT_SEARCH_FILTER,
        recruitmentType: value as RecruitmentType,
        freeWords: [],
        skills: [],
      });
    },
    [methods],
  );

  return (
    <div className="text-base-content mt-[56px] w-full">
      <div className="col-span-3 row-span-3 flex flex-shrink-0 flex-col svelte-1n6ue57">
        <div className="bg-base-100 grid w-full flex-grow gap-3 rounded-xl p-6 shadow-xl relative">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 btn btn-ghost btn-sm btn-circle hover:text-base-content hover:bg-[#0F172A]"
            aria-label="検索フィルターを閉じる"
          >
            <XIcon className="w-8 h-8 text-red-[#0F172A] hover:text-white" />
          </button>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSearch)}>
              <FormGroupUI className="mb-6">
                <FormGroupLabelUI>検索対象</FormGroupLabelUI>
                <ReactSelectUI
                  registerName="recruitmentType"
                  className="w-40"
                  options={recruitmentTypeOptions}
                  isSetDefault={true}
                  onValueChange={handleRecruitmentTypeChange}
                />
              </FormGroupUI>
              {isMatter ? <MatterTypeFormUI /> : <HRTypeFormUI />}
              <div className="flex justify-center space-x-5 mt-5">
                <ButtonUI
                  data-testid="search-btn"
                  type="submit"
                  className="w-40"
                  disabled={isLoading}
                >
                  検索
                </ButtonUI>
                <ButtonUI
                  type="button"
                  isOutline={true}
                  className="w-40"
                  disabled={isLoading}
                  onClick={() => {
                    onClearFilter();
                  }}
                >
                  クリア
                </ButtonUI>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};
